import * as Mui from "@mui/material";
import { ReactNode, useMemo } from "react";
import { isArray } from "underscore";

export interface AutoCompleteOption {
  value: string | number | boolean;
  label: any;
}

export interface AutoCompleteProps<OptionRetType> {
  renderInput?: (params: Mui.AutocompleteRenderInputParams) => React.ReactNode;
  label?: string;
  options: AutoCompleteOption[];
  fullWidth?: boolean;
  value?: OptionRetType;
  onChange?: (value: OptionRetType) => void;
  disabled?: boolean;
  placeholder?: string;
  disableClearable?: boolean;
  multiple?: boolean;
  error?: string | boolean | ReactNode;
  helperText?: any;
  variant?: Mui.TextFieldProps["variant"];
  disablePortal?: boolean;
  getOptionDisabled?: (value: any) => boolean;
  limitTags?: number;
  sx?: Mui.SxProps<Mui.Theme>;
}

const AutoComplete = <
  OptionRetType extends
    | (string | number | boolean)
    | (string | number | boolean)[] = string
>(
  props: AutoCompleteProps<OptionRetType>
) => {
  const onSelectChange = (
    _event: any,
    option: AutoCompleteOption | AutoCompleteOption[] | null
  ) => {
    if (props.onChange) {
      if (isArray(option)) {
        props.onChange(option?.map((opt) => opt.value) as OptionRetType);
      } else {
        props.onChange(option?.value as OptionRetType);
      }
    }
  };

  const selectValue: AutoCompleteOption | AutoCompleteOption[] | null =
    useMemo(() => {
      const value = props.value;
      if (value && isArray(value)) {
        return props.options.filter((opt) => value.includes(opt.value));
      }
      return props.options.find((opt) => opt.value === value) || null;
    }, [props.value, props.options]);

  return (
    <Mui.Autocomplete
      multiple={props.multiple}
      limitTags={props.limitTags}
      getOptionDisabled={props.getOptionDisabled}
      options={props.options}
      value={selectValue}
      disablePortal={props.disablePortal}
      sx={props.sx}
      onChange={onSelectChange}
      disabled={props.disabled}
      disableClearable={props.disableClearable}
      renderOption={(props, option, state) => {
        return (
          <li {...props} key={props.id}>
            {option.label}
          </li>
        )
      }}
      renderInput={(params) => {
        if (props.renderInput) return props.renderInput(params);
        return (
          <Mui.TextField
            {...params}
            label={props.label}
            variant={props.variant || "standard"}
            fullWidth={props.fullWidth || true}
            placeholder={props.placeholder}
            error={props.error ? true : false}
            helperText={!!props.helperText ? props.helperText : props.error}
          />
        );
      }}
      // popupIcon={<MuiIcons.KeyboardArrowDown />}
    />
  );
};

export default AutoComplete;
